import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';

// Form validation schema
const addUserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  clinicianTitle: z
    .string()
    .min(1, 'Clinician title is required')
    .max(10, 'Title must be 10 characters or less'),
});

type AddUserFormData = z.infer<typeof addUserSchema>;

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddUserDialog: React.FC<AddUserDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { addNewUser, showSuccess, showError } = useStore(
    useShallow(state => ({
      addNewUser: state.addNewUser,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddUserFormData>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      clinicianTitle: '',
    },
  });

  const onSubmit = async (data: AddUserFormData) => {
    try {
      await addNewUser({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        clinician_title: data.clinicianTitle,
      });
      showSuccess('User created successfully');
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create user:', error);
      showError('Unable to create user. Please check the form and try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <label htmlFor='firstName' className='text-sm font-medium'>
                First Name
              </label>
              <Input
                id='firstName'
                {...register('firstName')}
                placeholder='Enter first name'
                aria-invalid={errors.firstName ? 'true' : 'false'}
              />
              {errors.firstName && (
                <p className='text-sm text-red-600'>
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className='space-y-2'>
              <label htmlFor='lastName' className='text-sm font-medium'>
                Last Name
              </label>
              <Input
                id='lastName'
                {...register('lastName')}
                placeholder='Enter last name'
                aria-invalid={errors.lastName ? 'true' : 'false'}
              />
              {errors.lastName && (
                <p className='text-sm text-red-600'>
                  {errors.lastName.message}
                </p>
              )}
            </div>
            <div className='space-y-2'>
              <label htmlFor='email' className='text-sm font-medium'>
                Email
              </label>
              <Input
                id='email'
                type='email'
                {...register('email')}
                placeholder='Enter email'
                aria-invalid={errors.email ? 'true' : 'false'}
              />
              {errors.email && (
                <p className='text-sm text-red-600'>{errors.email.message}</p>
              )}
            </div>
            <div className='space-y-2'>
              <label htmlFor='clinicianTitle' className='text-sm font-medium'>
                Clinician Title
              </label>
              <Input
                id='clinicianTitle'
                {...register('clinicianTitle')}
                placeholder='Enter title (e.g., RN, PT, OT)'
                aria-invalid={errors.clinicianTitle ? 'true' : 'false'}
              />
              {errors.clinicianTitle && (
                <p className='text-sm text-red-600'>
                  {errors.clinicianTitle.message}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
